import Breadcrumb from 'components/commercetools-ui/atoms/breadcrumb';
import Button from 'components/commercetools-ui/atoms/button';
import Dropdown from 'components/commercetools-ui/atoms/dropdown';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import Gallery from 'components/commercetools-ui/organisms/gallery';
import ProductInformation from 'components/commercetools-ui/organisms/product/product-details/components/product-information';
import useTrack from 'components/commercetools-ui/organisms/product/product-details/hooks/useTrack';
import { UIProduct } from 'components/commercetools-ui/organisms/product/product-details/types';
import { useAddToCartOverlay } from 'context/add-to-cart-overlay';
import { useAccount, useCart, useProduct } from 'frontastic';
import { PDP_PRODUCT_ADDED_TO_CART } from 'helpers/constants/events';
import useClassNames from 'helpers/hooks/useClassNames';
import { useFormat } from 'helpers/hooks/useFormat';
import useGetUserInfo from 'helpers/hooks/useGetUserInfo';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import usePath from 'helpers/hooks/usePath';
import { desktop } from 'helpers/utils/screensizes';
import { useParams, useSearchParams } from 'next/navigation';
import { getTranslationLanguages } from 'project.config';
import { FC, memo, useCallback, useEffect, useState } from 'react';
import { Variant } from 'shared/types/product';
import { Category } from 'shared/types/product/Category';
import { ProjectConfig } from 'types/project-config';
import UserAvatar from './avatar';

interface SelectOption {
  label: string;
  value: string;
}

export interface ProductDetailsCertificationCardsProps {
  product: UIProduct;
  variant: Variant;
  url?: string;
  onChangeVariant: (sku: string) => void;
  inModalVersion?: boolean;
  setIsOpen?: (value: boolean) => void;
  onAddToCart?: () => void;
  projectConfig: ProjectConfig;
}

interface PadiCertification {
  cardGroupCode: string;
  cardStockImageUrl: string;
  certificationCode: string;
  certificationDate: string;
  certificationId: number;
  certificationNumber: string;
  credentialName: string;
  name: string;
}

// Default messages for the avatar section.
const photoInfoMessage =
  'This photo will appear on your replacement certification card or eCard. If you don\'t see a photo, or if you’d like to upload a new image, click "Upload Photo" to upload a JPG or PNG.';
const noPhotoAlert =
  'We currently have no photo attached to your profile. For certification cards, we require a photo.';
const ProductDetailsCertificationCards: FC<ProductDetailsCertificationCardsProps> = ({
  product,
  variant,
  url,
  onChangeVariant,
  inModalVersion,
  setIsOpen,
  onAddToCart,
}) => {
  const { addItem } = useCart();
  const { formatMessage } = useFormat({ name: 'cart' });
  const { idToken, userData } = useGetUserInfo();
  const { loggedIn, getProjectConfig } = useAccount();
  const [isDesktopSize] = useMediaQuery(desktop);
  const [category, setCategory] = useState<Category>();
  const [quantity, setQuantity] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [added, setAdded] = useState(false);
  const { path } = usePath();
  const { locale } = useParams();
  const searchParams = useSearchParams();
  const { formatMessage: formatMessageCart } = useFormat({ name: 'cart' });
  const { categories } = useProduct();
  const { trackAddToCart } = useTrack({ product, inModalVersion });

  const { show, fetchRelatedProducts } = useAddToCartOverlay();

  const wrapperClassName = inModalVersion
    ? 'md:grid grid-cols-12 pt-70 pb-35 px-20 md:pr-36'
    : 'pt-20 md:pt-24 pb-32 md:grid md:grid-cols-12 md:items-start mx-auto max-w-7xl px-24 lg:px-32';

  const galleryContainerClassName = useClassNames([
    inModalVersion ? 'col-span-6' : 'md:col-span-7 lg:col-span-8',
    'md:pr-26 lg:pr-60',
  ]);

  const informationContainerClassName = useClassNames([
    inModalVersion ? 'col-span-6' : 'md:col-span-5 lg:col-span-4',
    'mt-24 md:mt-0',
  ]);

  const [hasAvatar, setHasAvatar] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [formSelectedCertificateId, setFormSelectedCertificateId] = useState<string>('');
  const [formSelectedCertificateName, setFormSelectedCertificateName] = useState<string>('');

  const [selectCertOptions, setSelectCertOptions] = useState<SelectOption[]>([]);

  const handleAddToCart = async () => {
    setLoading(true);

    const custom = {
      type: {
        key: 'certification',
        typeId: 'type',
      },

      fields: {
        certificationId: formSelectedCertificateId,
        certificationName: formSelectedCertificateName,
      },
    };

    addItem(product, variant, quantity, custom).then(() => {
      setLoading(false);
      setAdded(true);
      setTimeout(() => {
        setAdded(false);
        setQuantity(1);
      }, 1000);
      show(product, variant, quantity);
      onAddToCart?.();
    });

    trackAddToCart(quantity);
  };

  const onLoginClick = async () => {
    const urlLocaleParts = locale.split('-');
    const twoDigitCodeLanguage = urlLocaleParts[0];
    const languages = getTranslationLanguages();
    const language = languages[twoDigitCodeLanguage]?.languageCode;
    const ssoAccountDomain = await getProjectConfig('EXTENSION_PADI_APIS_SSO_ACCOUNT_DOMAIN');
    const clientId = await getProjectConfig('EXTENSION_COMMERCETOOLS_COGNITO_CLIENT_ID');
    const query = searchParams.toString();
    let lvp = `${path}?${query}`;
    if (path && path.startsWith('/')) {
      lvp = lvp.substring(1);
    }
    const ssoLink = `${ssoAccountDomain.setting}/login/sign-in/${language}?client_id=${clientId.setting}&redirect_uri=${window.location.origin}/${locale}/padi-sso/callback&extraData=lvp=${lvp}`;
    window.location.href = ssoLink;
  };
  // Populate dropdown with users certificates.
  const updateCertificationOptions = useCallback(async (certifications: PadiCertification[]) => {
    if (!certifications) return;
    const certOptions: SelectOption[] = [{ label: '', value: '' }];
    const nonSellableCerts = [-1, 0, 5, 11, 13, 14, 22, 58, 99, 100, 3391, 4500];
    certifications.forEach((certification) => {
      const certificationId = certification.certificationId;
      const certificationName = certification.credentialName;
      if (!nonSellableCerts.includes(certificationId)) {
        certOptions.push({
          label: certificationName,
          value: `${certificationId}`,
        });
      }
    });
    setSelectCertOptions(certOptions);
  }, []);
  const [userCertifications, setUserCertifications] = useState<PadiCertification[] | undefined>(undefined);

  // Get users certificates.
  const getUserCertifications = useCallback(
    async (idToken: string) => {
      if (idToken) {
        const proApiDomain = await getProjectConfig('EXTENSION_PADI_APIS_PRO_API_DOMAIN');
        if (proApiDomain.isError) return;
        const getCerts = await fetch(`${proApiDomain.setting}/certifications/me`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
        });
        const certs = await getCerts.json();
        setUserCertifications(certs.items);
        if (certs?.items) {
          updateCertificationOptions(certs.items);
        }
        if (certs?.certificationPhotoUri) {
          setHasAvatar(true);
        }
        setIsLoading(false);
      }
    },
    [getProjectConfig, updateCertificationOptions],
  );

  const handleCertificationChange = async (e: React.FormEvent) => {
    const newValue = (e.target as HTMLSelectElement).value;

    setFormSelectedCertificateId(newValue.toString());
    userCertifications?.some((cert) => {
      if (cert.certificationId.toString() == newValue) {
        setFormSelectedCertificateName(cert.credentialName);
      }
    });
  };

  const onAvatarUpdate = (hasAvatar: boolean) => {
    setHasAvatar(hasAvatar);
  };

  useEffect(() => {
    if (idToken) {
      if (userCertifications === undefined) getUserCertifications(idToken);
    }
  }, [userCertifications, getUserCertifications, idToken, userData]);

  useEffect(() => {
    if (product && categories && !category) {
      const currentCategory = categories.find((cat) => cat.categoryId === product.categories?.[0]?.categoryId);
      setCategory(currentCategory);
    }
  }, [categories, category, product, product?.categories]);

  useEffect(() => {
    fetchRelatedProducts(product);
  }, [product, fetchRelatedProducts]);

  return (
    <div className={wrapperClassName}>
      {category && isDesktopSize && !inModalVersion && (
        <Breadcrumb Separator="/" className="col-span-12 mb-24 w-fit">
          <Link key={category.categoryId} link={category._url} className="text-14 text-primary-black">
            {category.name}
          </Link>

          <Typography key={product.slug} className="cursor-default text-14 text-neutral-500">
            {product.name}
          </Typography>
        </Breadcrumb>
      )}

      <div className={galleryContainerClassName}>
        <Gallery
          images={variant?.images ?? []}
          imagesExtra={variant?.imagesExtra ?? []}
          inModalVersion={inModalVersion}
          firstImagePriority={true}
          hideArrowsOnSingleSlide={true}
        />
      </div>

      <div className={informationContainerClassName}>
        <ProductInformation
          product={product}
          variant={variant}
          onChangeVariant={onChangeVariant}
          inModalVersion={inModalVersion}
        />

        {!loggedIn ? (
          <div className="flex flex-col gap-32 pt-20">
            <div>
              <a className="pr-6 text-padi-blue hover:text-padi-blue-darker" onClick={onLoginClick}>
                Sign in
              </a>
              to purchase a replacement card.
            </div>
            <p>
              PADI certified but do not have a PADI account?
              <a className="pl-6 text-padi-blue hover:text-padi-blue-darker" href="https://www.padi.com/login/lookup/">
                Find your certifications
              </a>
            </p>

            <Button
              className="w-full rounded-sm border-[#D8D8DA] text-14 font-medium"
              variant="secondary"
              onClick={onLoginClick}
              dataAnalytics={PDP_PRODUCT_ADDED_TO_CART}
            >
              {formatMessage({ id: 'sign.in', defaultMessage: 'Sign in' })}
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-8 pt-20">
            <label className="font-medium" tabIndex={0}>
              {formatMessageCart({ id: 'cart.select_cert', defaultMessage: 'Select Certification' })}
            </label>
            <Dropdown
              className="h-full rounded-sm"
              value={formSelectedCertificateId.toString()}
              items={selectCertOptions}
              onChange={handleCertificationChange}
              disabled={isLoading}
            />
            {!isLoading && (
              <UserAvatar
                hasAvatar={hasAvatar}
                onAvatarUpdate={onAvatarUpdate}
                photoInfoMessage={photoInfoMessage}
                noPhotoAlert={noPhotoAlert}
              />
            )}

            <Button
              className="w-full rounded-sm text-14 font-medium"
              variant="primary"
              onClick={handleAddToCart}
              loading={isLoading}
              added={added}
              dataAnalytics={PDP_PRODUCT_ADDED_TO_CART}
              disabled={!formSelectedCertificateId || !hasAvatar ? true : false}
            >
              {formatMessageCart({ id: 'cart.add', defaultMessage: 'Add to cart' })}
            </Button>
            <div className="mt-10"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(ProductDetailsCertificationCards);
